import { useCallback, useEffect, useRef, useState } from 'react'
import { postSubmit } from '@ui-seeker/utils'

import { BACKEND_APIS } from '@constants'
import { createLatestGate, shouldPaint } from '../../../../../../@components/KindPillsField/latestOnly'
import { bodyKey } from '../@services/request'


// the session's details: reopening one is instant; an error is never cached
const CACHE = new Map()

/**
 * A detail of "Price curve of a date" (a provider by category / Acriss, the prices of a day): asks when `body` appears,
 * paints only the latest answer, ignores a cancel and lets "Try again" ask again. `body` null = nothing open. The
 * pattern of the cell curve (`useCellCurve`).
 */
export default function useCachedResult(body) {
	const key = bodyKey(body)
	const [state, setState] = useState(() => ({ key, data: key ? CACHE.get(key) || null : null, loading: false, error: null }))
	const gate = useRef(createLatestGate())
	const [attempt, setAttempt] = useState(0)

	useEffect(() => {
		if (!key) return undefined
		if (CACHE.has(key)) {
			setState({ key, data: CACHE.get(key), loading: false, error: null })
			return undefined
		}
		const ctrl = new AbortController()
		const id = gate.current.next()
		setState({ key, data: null, loading: true, error: null })
		postSubmit(`${BACKEND_APIS.DASHBOARD}/results`, JSON.parse(key), { signal: ctrl.signal, retries: 1 })
			.then(data => {
				CACHE.set(key, data)
				if (shouldPaint(gate.current, id)) setState({ key, data, loading: false, error: null })
			})
			.catch(error => {
				if (shouldPaint(gate.current, id, error)) setState({ key, data: null, loading: false, error: error || new Error('request failed') })
			})
		return () => ctrl.abort()
	}, [key, attempt])

	const retry = useCallback(() => setAttempt(n => n + 1), [])
	const current = state.key === key ? state : { key, data: null, loading: !!key, error: null }
	return { data: current.data, loading: current.loading, error: current.error, retry }
}
